import FormControl from "common/components/FormControl/FormControl";
import CrudForm from "common/components/Crud/CrudForm";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import RadioBlockItem from "common/components/RadioBlockItem/RadioBlockItem";
import ProductSelect from "components/select/ProductSelect/ProductSelect";
import useList from "hooks/useList";
import { useMemo } from "react";
import { Field } from "react-final-form";
import { useTranslation } from "react-i18next";
import type { Organization, Subscription } from "types";
import PlanSelect from "components/select/PlanSelect/PlanSelect";
import PlanCheckboxList from "components/select/PlanSelect/PlanCheckboxList";

type Props = {
  onSubmit: (values: any) => any;
  onCancel?: () => void;
  item?: Organization;
};

function OrganizationForm({ onSubmit, onCancel, item }: Props) {
  const { t } = useTranslation();
  const { items: subscriptions } = useList<Subscription>("subscriptions");

  const initialValues = useMemo(
    () =>
      item?.id
        ? {
            ...item,
            products: item?.products?.map((p) => p.id),
            plans: item?.plans?.map((p) => p.id),
            subscription: item?.subscription?.id,
          }
        : {
            name: "",
          },
    [item],
  );

  const SUBSCRIPTION_ICONS = useMemo(
    () => ["BsStar", "BsStarHalf", "BsStarFill"] as const,
    [],
  );

  const interpolateColor = (a: number) => {
    const r = Math.round(0x00 + a * (0x98 - 0x00));
    const g = Math.round(0x9f + a * (0xcc - 0x9f));
    const b = Math.round(0xed + a * (0x33 - 0xed));
    return `#${r.toString(16).padStart(2, "0")}${g
      .toString(16)
      .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  };

  const subscriptionItems = useMemo(() => {
    const list = subscriptions ?? [];
    return list.map((s, index) => {
      const a = list.length > 1 ? index / (list.length - 1) : 0;
      const color =
        index === 0
          ? "secondary"
          : index === list.length - 1
          ? "ternary"
          : interpolateColor(a);
      return {
        id: s.id,
        title: s.name,
        subtitle: s.description,
        icon: SUBSCRIPTION_ICONS[index] ?? "BsStarFill",
        color,
      };
    });
  }, [SUBSCRIPTION_ICONS, subscriptions]);

  return (
    <CrudForm
      onSubmit={onSubmit}
      onCancel={onCancel}
      initialValues={initialValues}
      headerIcon="BuildingOfficeIcon"
      i18nNamespace="organizations"
      render={() => (
        <div className="flex flex-col gap-4 white-box">
          <div className="flex flex-row gap-4">
            <FormControl
              name="logo"
              titleKey="organizations.logo"
              type="image"
              labelIcon="PhotoIcon"
              containerStyle="min-w-[200px]"
              className="border border-slate-200 rounded-lg overflow-hidden"
            />

            <div className="flex flex-col flex-1 gap-2">
              <FormControl
                name="name"
                title={t("organizations.name")}
                required
              />

              {subscriptionItems.length > 0 && (
                <Field
                  name="subscription"
                  defaultValue={subscriptionItems[0]?.id}
                >
                  {({ input }) => (
                    <div>
                      <span className="text-sm font-bold text-slate-600">
                        {t("organizations.subscription")}
                      </span>
                      <RadioBlock
                        items={subscriptionItems}
                        value={input.value || undefined}
                        onChange={input.onChange}
                        Component={RadioBlockItem}
                        horizontal
                      />
                    </div>
                  )}
                </Field>
              )}
            </div>
          </div>

          <FormControl
            name="products"
            title={t("organizations.products")}
            type="custom"
          >
            <ProductSelect multiple />
          </FormControl>

          <FormControl
            name="plans"
            title={t("organizations.plans")}
            type="custom"
          >
            <PlanCheckboxList />
          </FormControl>
        </div>
      )}
    />
  );
}

export default OrganizationForm;
